import loopback from './loopback'
import { getSuggestionTiers } from './pricing-offers'
import {
  convertSecondsToCustomTimeFormat,
  formatToCustomDate,
  getDatetimeOnBeginningOfTheMonthByMonthsAgo,
  getDatetimeOnEndOfTheMonthByMonthsAgo
} from '../utils/datetime'

function getCdrsFromApi (filter, page = 1, timeout = 200) {
  return new Promise(async (resolve, reject) => {
    return loopback
      .get([
        '/calls',
        `?${filter}`,
        `&page=${page}`,
        '&filter[limit]=1000'
      ].join(''))
      .then(cdrs => {
        if (cdrs && cdrs.data) {
          const dataFromAPI = Array.from(cdrs.data)
          if (cdrs.meta && cdrs.meta.nextPage) {
            return setTimeout(() => {
              return getCdrsFromApi(filter, cdrs.meta.nextPage).then(data => {
                return resolve([...dataFromAPI, ...data])
              }).catch(reject)
            }, timeout)
          } else {
            return resolve(dataFromAPI)
          }
        }
      })
      .catch((err) => {
        return reject(err)
      })
  })
}

export async function retrieveDataFromAPI (filters) {
  return new Promise((resolve, reject) => {
    let filter = ''
    if (filters.startedAt) { filter += `filter[where][and][0][started_at][gt]=${new Date(filters.startedAt).toISOString()}&` }
    if (filters.endedAt) { filter += `filter[where][and][1][ended_at][lt]=${new Date(filters.endedAt).toISOString()}&` }
    if (filters.callType) { filter += `filter[where][and][2][call_type]=${filters.callType}&` }
    filter += 'filter[order]=started_at asc'
    return getCdrsFromApi(filter).then(resolve).catch(reject)
  })
}

const usageMonthly = {}

export function getInitialSelectedFilterDate () {
  return JSON.stringify({
    name: 'last1Months',
    monthAgo: 1,
    startedAt: getDatetimeOnBeginningOfTheMonthByMonthsAgo(1).toISOString(),
    endedAt: getDatetimeOnEndOfTheMonthByMonthsAgo(1).toISOString()
  })
}

export function getFilterDateOptions () {
  return Array.from({ length: 6 }, (_, index) => index).map(month => {
    return {
      text: formatToCustomDateWithUpperCase(getDatetimeOnBeginningOfTheMonthByMonthsAgo(month)),
      value: JSON.stringify({
        name: `last${month}Months`,
        monthAgo: month,
        startedAt: getDatetimeOnBeginningOfTheMonthByMonthsAgo(month).toISOString(),
        endedAt: getDatetimeOnEndOfTheMonthByMonthsAgo(month).toISOString()
      })
    }
  })
}

export function getMonthName (month) {
  return formatToCustomDateWithUpperCase(getDatetimeOnBeginningOfTheMonthByMonthsAgo(month))
}

function getExtensionsUsage (cdrs) {
  const extensionsUsage = {}
  cdrs.forEach(cdr => {
    if (!extensionsUsage[cdr.from]) {
      extensionsUsage[cdr.from] = {
        seconds: 0,
        cost: 0,
        firstName: '',
        lastName: '',
        email: ''
      }
    }
    if (Number(cdr.call_price) > 0.0) {
      extensionsUsage[cdr.from].seconds += Number(cdr.duration)
      extensionsUsage[cdr.from].cost += Number(cdr.call_price)
    }
    if (!extensionsUsage[cdr.from].firstName) {
      extensionsUsage[cdr.from].firstName = cdr.first_name
    }
    if (!extensionsUsage[cdr.from].lastName) {
      extensionsUsage[cdr.from].lastName = cdr.last_name
    }
    if (!extensionsUsage[cdr.from].email) {
      extensionsUsage[cdr.from].email = cdr.email
    }
  })
  return extensionsUsage
}

function getExtensionsUsageByTier (extensionsUsage, tier, filter) {
  return Object.entries(extensionsUsage).reduce((acc, [extension, extensionUsage]) => {
    if (!extensionUsage) {
      return acc
    }
    const minutes = Number(convertSecondsToCustomTimeFormat(extensionUsage.seconds, 'mm'))
    const [min, max] = tier.minutesRange
    const isInRange = minutes >= min && minutes <= max
    if (isInRange) {
      let name = extensionUsage.firstName || extension
      if (extensionUsage.lastName) {
        name += ` ${extensionUsage.lastName}`
      }
      acc.push({
        extension,
        name,
        email: extensionUsage.email,
        seconds: extensionUsage.seconds,
        cost: extensionUsage.cost.toFixed(2),
        variability: (tier.monthlyFee - extensionUsage.cost).toFixed(2),
        monthly: formatToCustomDateWithUpperCase(getDatetimeOnBeginningOfTheMonthByMonthsAgo(filter.monthAgo)),
        monthlyFee: tier.monthlyFee
      })
    }
    return acc
  }, [])
}

function sortTiersBySpecificity (tiers) {
  return [...tiers].sort((a, b) => {
    const rangeA = a.minutesRange ? (a.minutesRange[1] - a.minutesRange[0]) : 0
    const rangeB = b.minutesRange ? (b.minutesRange[1] - b.minutesRange[0]) : 0
    return rangeB - rangeA
  })
}

function collectExistingUsers (usageData) {
  const emails = new Set()

  Object.values(usageData).forEach(group => {
    if (Array.isArray(group)) {
      group.forEach(user => {
        if (user && user.email) {
          emails.add(user.email.trim().toLowerCase())
        }
      })
    }
  })

  return { emails }
}

function createManagerUser (name, email, monthlyFee, filter) {
  return {
    name,
    email,
    seconds: 0,
    cost: '0.00',
    variability: monthlyFee.toFixed(2),
    monthly: formatToCustomDateWithUpperCase(getDatetimeOnBeginningOfTheMonthByMonthsAgo(filter.monthAgo)),
    monthlyFee: monthlyFee
  }
}

export function processUsersMonthlyUsage (filter, pricingList = []) {
  return new Promise(async (resolve, reject) => {
    try {
      const yearMonth = formatToCustomDateWithUpperCase(getDatetimeOnBeginningOfTheMonthByMonthsAgo(filter.monthAgo))
      const suggestionTiers = getSuggestionTiers(pricingList)

      if (!usageMonthly[yearMonth]) {
        usageMonthly[yearMonth] = suggestionTiers.reduce((groups, tier) => {
          groups[tier.code] = []
          return groups
        }, {})
      } else {
        return resolve(usageMonthly[yearMonth])
      }

      const cdrs = await retrieveDataFromAPI(filter)
      const extensionsUsage = cdrs && cdrs.length > 0 ? getExtensionsUsage(cdrs) : {}

      if (Object.keys(extensionsUsage).length > 0 && suggestionTiers.length > 0) {
        const sortedTiers = sortTiersBySpecificity(suggestionTiers)

        sortedTiers.forEach(tier => {
          const availableExtensionsUsage = Object.fromEntries(Object.entries(extensionsUsage))
          const tierUsers = getExtensionsUsageByTier(availableExtensionsUsage, tier, filter)
          usageMonthly[yearMonth][tier.code] = tierUsers
        })
      }

      const existing = collectExistingUsers(usageMonthly[yearMonth])

      const allUsers = await loopback.get('/users?filter[order]=name asc')
      if (allUsers && Array.isArray(allUsers)) {
        const managerTier = pricingList.find(tier => tier.code === 'manager')
        const managerMonthlyFee = managerTier ? managerTier.monthlyFee : 0

        const activeUsersWithoutListing = allUsers
          .filter(user => {
            const status = user.status != null ? String(user.status).trim().toUpperCase() : ''
            const isActive = status !== 'INACTIVE'
            const email = user.email && user.email.trim().toLowerCase()
            return isActive && email && !existing.emails.has(email)
          })
          .map(user => {
            const email = user.email.trim()
            existing.emails.add(email.toLowerCase())
            return createManagerUser(user.name || user.email, email, managerMonthlyFee, filter)
          })

        if (activeUsersWithoutListing.length > 0) {
          usageMonthly[yearMonth].manager = (usageMonthly[yearMonth].manager || []).concat(activeUsersWithoutListing)
        }
      }

      return resolve(usageMonthly[yearMonth])
    } catch (err) {
      return reject(err)
    }
  })
}

function formatToCustomDateWithUpperCase (datetime) {
  const formatted = formatToCustomDate(datetime, 'UTC', 'MMM - YYYY')
  return formatted.charAt(0).toUpperCase() + formatted.slice(1)
}



// WEBPACK FOOTER //
// ./src/services/pricing.js